import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Add from '@mui/icons-material/esm/Add';
import ChangeCircle from '@mui/icons-material/esm/ChangeCircle';
import CheckCircle from '@mui/icons-material/esm/CheckCircle';
import HourglassEmpty from '@mui/icons-material/esm/HourglassEmpty';
import Rule from '@mui/icons-material/esm/Rule';
import { KPICard, DataTable, Column } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import changeRequestsService, { ChangeRequest, ChangeRequestStatus, ChangeRequestType } from '../../services/api/changeRequestsService';

const statusColor: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  in_review: 'warning',
  approved: 'info',
  rejected: 'error',
  enacted: 'success',
  cancelled: 'default',
};

const typeColor: Record<string, 'primary' | 'secondary' | 'success' | 'warning'> = {
  scope: 'primary',
  cost: 'success',
  schedule: 'warning',
  mixed: 'secondary',
};

const labelize = (value: string) => value.replace(/_/g, ' ');

export default function ChangeRequestsPage() {
  const navigate = useNavigate();
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const [rows, setRows] = useState<ChangeRequest[]>([]);
  const [status, setStatus] = useState<ChangeRequestStatus | ''>('');
  const [changeType, setChangeType] = useState<ChangeRequestType | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    changeType: 'mixed' as ChangeRequestType,
    estimatedCostImpact: '',
    estimatedScheduleImpactDays: '',
  });

  const load = async () => {
    if (!activeProjectId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await changeRequestsService.list({
        projectId: activeProjectId,
        status,
        changeType,
      });
      setRows(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load change requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId, status, changeType]);

  const stats = useMemo(() => ({
    inReview: rows.filter((row) => row.status === 'in_review').length,
    approved: rows.filter((row) => row.status === 'approved').length,
    enacted: rows.filter((row) => row.status === 'enacted').length,
    rejected: rows.filter((row) => row.status === 'rejected').length,
  }), [rows]);

  const create = async () => {
    if (!activeProjectId) return;
    try {
      const created = await changeRequestsService.create({
        projectId: activeProjectId,
        title: form.title,
        description: form.description || null,
        changeType: form.changeType,
        estimatedCostImpact: form.estimatedCostImpact ? Number(form.estimatedCostImpact) : null,
        estimatedScheduleImpactDays: form.estimatedScheduleImpactDays ? Number(form.estimatedScheduleImpactDays) : null,
      });
      setDialogOpen(false);
      navigate(`/change-requests/${created.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to create change request');
    }
  };

  const columns: Column<ChangeRequest>[] = [
    { id: 'crCode', label: 'Code', minWidth: 110 },
    { id: 'title', label: 'Title', minWidth: 240 },
    {
      id: 'changeType',
      label: 'Type',
      minWidth: 110,
      format: (value: ChangeRequestType) => <Chip size="small" color={typeColor[value]} label={labelize(value)} />,
    },
    {
      id: 'computedCostImpact',
      label: 'Cost Impact',
      minWidth: 120,
      align: 'right',
      format: (value: number | string | null) => value == null ? '-' : `$${Number(value).toLocaleString()}`,
    },
    {
      id: 'computedScheduleImpactDays',
      label: 'Schedule Days',
      minWidth: 130,
      align: 'right',
      format: (value: number | null) => value ?? '-',
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      format: (value: ChangeRequestStatus) => <Chip size="small" color={statusColor[value]} label={labelize(value)} />,
    },
    {
      id: 'raisedAt',
      label: 'Raised',
      minWidth: 150,
      format: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  if (!activeProjectId) {
    return <Box><PageHeader title="Change Requests" /><Alert severity="info">Select a project to view change requests.</Alert></Box>;
  }

  return (
    <Box>
      <PageHeader
        title="Change Requests"
        subtitle={`${rows.length} request${rows.length === 1 ? '' : 's'}`}
        actions={<Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>New Change Request</Button>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}><KPICard title="In Review" value={`${stats.inReview}`} icon={<HourglassEmpty />} color="warning" /></Grid>
        <Grid item xs={12} md={3}><KPICard title="Awaiting Enact" value={`${stats.approved}`} icon={<Rule />} color="info" /></Grid>
        <Grid item xs={12} md={3}><KPICard title="Enacted" value={`${stats.enacted}`} icon={<CheckCircle />} color="success" /></Grid>
        <Grid item xs={12} md={3}><KPICard title="Rejected" value={`${stats.rejected}`} icon={<ChangeCircle />} color={stats.rejected > 0 ? 'error' : 'success'} /></Grid>
      </Grid>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <TextField select size="small" label="Status" value={status} onChange={(event) => setStatus(event.target.value as ChangeRequestStatus | '')} sx={{ minWidth: 170 }}>
          <MenuItem value="">All</MenuItem>
          {(['draft', 'in_review', 'approved', 'rejected', 'enacted', 'cancelled'] as ChangeRequestStatus[]).map((value) => <MenuItem key={value} value={value}>{labelize(value)}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Type" value={changeType} onChange={(event) => setChangeType(event.target.value as ChangeRequestType | '')} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          {(['scope', 'cost', 'schedule', 'mixed'] as ChangeRequestType[]).map((value) => <MenuItem key={value} value={value}>{labelize(value)}</MenuItem>)}
        </TextField>
      </Stack>
      <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
        <DataTable columns={columns} data={rows} loading={loading} searchable pagination onRowClick={(row) => navigate(`/change-requests/${row.id}`)} />
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Change Request</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
          <TextField label="Title" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
          <TextField select label="Type" value={form.changeType} onChange={(event) => setForm({ ...form, changeType: event.target.value as ChangeRequestType })}>
            {(['scope', 'cost', 'schedule', 'mixed'] as ChangeRequestType[]).map((value) => <MenuItem key={value} value={value}>{labelize(value)}</MenuItem>)}
          </TextField>
          <TextField label="Estimated Cost Impact" type="number" value={form.estimatedCostImpact} onChange={(event) => setForm({ ...form, estimatedCostImpact: event.target.value })} />
          <TextField label="Estimated Schedule Days" type="number" value={form.estimatedScheduleImpactDays} onChange={(event) => setForm({ ...form, estimatedScheduleImpactDays: event.target.value })} />
          <TextField label="Description" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} multiline minRows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.title.trim()} onClick={create}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
